import React, { forwardRef, useRef, useImperativeHandle } from "react";
import { Box, TextField, Typography } from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { TypographyPallet } from "../../utils/colorPallet";

interface IProps {
  title: string;
  length?: number;
  onSubmit: (values: string) => void;
  initialPin?: string;
}

const DigitalPinForm = forwardRef<{ submitForm: () => void }, IProps>(
  ({ title, length = 6, onSubmit, initialPin }, ref) => {
    const formRef = useRef<any>(null);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    useImperativeHandle(ref, () => ({
      submitForm: () => {
        formRef.current?.handleSubmit();
      },
    }));

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      index: number,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => void,
      values: string[]
    ) => {
      const value = e.target.value;
      if (/^\d$/.test(value)) {
        const newValue = [...values];
        newValue[index] = value;
        setFieldValue("pin", newValue);
        if (index < inputRefs.current.length - 1) {
          inputRefs.current[index + 1]?.focus();
        }
      }
    };

    const handleKeyDown = (
      e: React.KeyboardEvent<HTMLInputElement>,
      index: number,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean
      ) => void,
      values: string[]
    ) => {
      if (e.key === "Backspace") {
        const newValue = [...values];

        if (values[index] === "") {
          if (index > 0) {
            inputRefs.current[index - 1]?.focus();
            newValue[index - 1] = "";
          }
        } else {
          newValue[index] = "";
        }
        setFieldValue("pin", newValue);
      }
    };

    const validationSchema = Yup.object().shape({
      pin: Yup.array()
        .of(Yup.string().required("Each digit is required"))
        .min(length, `Please enter exactly ${length} digits`)
        .test("complete-pin", "Enter all digits", (value: any) =>
          value?.every((digit: any) => digit !== "")
        ),
    });

    return (
      <Formik
        innerRef={formRef}
        initialValues={{
          pin: initialPin ? initialPin.split("") : Array(length).fill(""),
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const pinCode = values.pin.join("");
          onSubmit(pinCode);
        }}
      >
        {({ setFieldValue, values, errors, touched }) => (
          <Form>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: { xs: 0.5, sm: 2 },
              }}
            >
              <Typography sx={{ ...TypographyPallet.pincss }}>
                {title}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 2.5 },
                  width: "100%",
                  maxWidth: "500px",
                }}
              >
                {Array.from({ length }).map((_, index) => (
                  <Field
                    key={index}
                    name={`pin[${index}]`}
                    as={TextField}
                    inputRef={(el: HTMLInputElement) =>
                      (inputRefs.current[index] = el)
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleInputChange(e, index, setFieldValue, values.pin)
                    }
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                      handleKeyDown(e, index, setFieldValue, values.pin)
                    }
                    inputProps={{
                      maxLength: 1,
                      style: {
                        width: "40px",
                        textAlign: "center",
                        fontSize: "18px",
                      },
                    }}
                    variant="outlined"
                    sx={{
                      flex: "1 1 10%",
                      minWidth: "40px",
                      maxWidth: "50px",
                    }}
                    error={Boolean(errors.pin && touched.pin)}
                  />
                ))}
              </Box>
              {errors.pin && touched.pin && (
                <Typography color="error" variant="body2">
                  {Array.isArray(errors.pin)
                    ? "Please ensure all fields are filled correctly."
                    : errors.pin}
                </Typography>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    );
  }
);

export default DigitalPinForm;
